import { type RefObject, useEffect } from 'react'

// Expand needs a real upward swipe so a slight flick against a collapsed name
// card does not bounce it open. Recollapse once compact is a shorter downward
// flick.
const COLLAPSE_THRESHOLD_PX = 48
const EXPAND_THRESHOLD_PX = 300

// The header stays expanded near the top of the list: collapsing there would
// fold rows away before the user has scrolled past them.
const COLLAPSE_MIN_SCROLL_PX = 64

// Matches the header's CSS transition. Flipping re-pads the scroll content, so
// the scroller keeps moving on its own for a frame or two afterwards (and the
// browser clamps scrollTop outright if the flip happened at the bottom of the
// list). Ignoring scroll for the length of the transition keeps that reflow
// from reading as a user scroll and bouncing the header back.
const SETTLE_MS = 240

// Drives the facesheet header's scroll collapse by writing `data-collapsed` on
// the header container. The header's own layout reacts in CSS
// (`group-data-[collapsed=true]/facesheet-header:`), so a scroll gesture never
// re-renders React — only the container's measured height does, once per flip.
export function useFacesheetHeaderCollapse(
  scroller: HTMLElement | null,
  headerRef: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    const header = headerRef.current
    if (!scroller || !header) {
      return
    }

    // `anchor` is the position the current direction run is measured from, not
    // where the gesture began: it trails the scroll while moving with the
    // current state, so a reversal only needs the threshold from the turning
    // point.
    let anchor = scroller.scrollTop
    let collapsed = false
    let frame = 0
    let settleTimer = 0
    let settling = false

    const flip = (next: boolean): void => {
      collapsed = next
      header.dataset.collapsed = String(next)
      anchor = scroller.scrollTop
      settling = true
      window.clearTimeout(settleTimer)
      settleTimer = window.setTimeout(() => {
        settling = false
        anchor = scroller.scrollTop
        // Re-check once the reflow has settled: a fling to the top during the
        // transition would otherwise leave the header collapsed at rest.
        measure()
      }, SETTLE_MS)
    }

    const measure = (): void => {
      if (settling) {
        return
      }
      const top = scroller.scrollTop
      // Collapsing in this band would fold rows away before the user has
      // scrolled past them. Expanding here used to be immediate too, which
      // bypassed the expand travel: a 50px flick back into this band bounced
      // the name card open. It now only auto-expands at the actual top, where
      // there is nothing left to scroll.
      if (top < COLLAPSE_MIN_SCROLL_PX && !collapsed) {
        anchor = top
        return
      }
      if (collapsed && top < 1) {
        flip(false)
        return
      }
      const delta = top - anchor
      if (
        collapsed ? delta < -EXPAND_THRESHOLD_PX : delta > COLLAPSE_THRESHOLD_PX
      ) {
        flip(!collapsed)
        return
      }
      if (collapsed ? delta > 0 : delta < 0) {
        anchor = top
      }
    }

    const onScroll = (): void => {
      // Coalesce to one read per frame: scroll fires far more often, and
      // reading scrollTop per event forces layout each time.
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0
          measure()
        })
      }
    }

    header.dataset.collapsed = 'false'
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      scroller.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(frame)
      window.clearTimeout(settleTimer)
      delete header.dataset.collapsed
    }
  }, [scroller, headerRef])
}
